import React from "react";
import "../../App.scss";
import { PACKAGING_TYPES, UNIT_TYPES, WAREHOUSE_STOCK_TYPES, resolveEnum } from '../../Constants';
import { Table, Button, Modal, Form } from "react-bootstrap";
import AuthorizedPage from "../../component/AuthorizedPage";
import ProductApprovalApi from "../../api/ProductApprovalApi";
import { updateState } from "../StateHelper";
import { Role } from "../../domain/Role";

interface PendingProduct {
  id?: number;
  name?: string;
  stockCode?: string;
  barcode?: string;
  notes?: string;
  packagingType?: string;
  packagingUnitType?: string;
  packagingUnitTypeAmount?: string;
  warehouseStockType?: string;
  frozen?: boolean;
  width?: string;
  length?: string;
  height?: string;
}

interface State {
  pendingProducts: PendingProduct[];
  pendingProductsRaw: PendingProduct[];
  selectedProduct: PendingProduct | null;
  showDetailModal: boolean;
  page: number;
  totalPages: number;
  search: string;
  pageSize: number;
}

const initialState: State = {
  pendingProducts: [],
  pendingProductsRaw: [],
  selectedProduct: null,
  showDetailModal: false,
  page: 0,
  totalPages: 0,
  search: "",
  pageSize: 50
};

const PAGE_SIZE_OPTIONS = [20, 50, 100, 200];

class ProductApproval extends React.Component<any, State> {
  state = initialState;
  searchTimer: any;

  componentDidMount() {
    this.loadPendingProducts();
  }

  // Server-side: arama + sayfa backend'e gider, filtreli + sayfali doner
  loadPendingProducts = async (page: number = this.state.page, search: string = this.state.search) => {
    const response = await ProductApprovalApi.listPendingPaged(search, page, this.state.pageSize);
    if (response.success && response.data) {
      this.setState({
        pendingProducts: response.data.content || [],
        totalPages: response.data.totalPages || 0,
        page: response.data.number || 0,
      });
    }
  };

  onSearchChange = (value: string) => {
    this.setState({ search: value });
    clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => this.loadPendingProducts(0, value), 350);
  };

  onPageSizeChange = (size: number) => {
    this.setState({ pageSize: size }, () => this.loadPendingProducts(0, this.state.search));
  };

  renderPagination = () => {
    const { page, totalPages, pageSize } = this.state;
    return (
      <div className="d-flex justify-content-center align-items-center mt-3 mb-3">
        <Button size="sm" variant="outline-secondary" className="me-3" disabled={page <= 0} onClick={() => this.loadPendingProducts(page - 1)}>← Önceki</Button>
        <span>Sayfa {totalPages === 0 ? 0 : page + 1} / {totalPages}</span>
        <Button size="sm" variant="outline-secondary" className="ms-3" disabled={page >= totalPages - 1} onClick={() => this.loadPendingProducts(page + 1)}>Sonraki →</Button>
        <Form.Select
          size="sm"
          className="ms-4"
          style={{ width: "auto" }}
          value={pageSize}
          onChange={(e) => this.onPageSizeChange(Number(e.target.value))}
        >
          {PAGE_SIZE_OPTIONS.map((n) => (
            <option key={n} value={n}>{n} / sayfa</option>
          ))}
        </Form.Select>
      </div>
    );
  };

  openDetailModal = async (product: PendingProduct) => {
    if (product.id === undefined) return;
    const response = await ProductApprovalApi.getPendingById(product.id);
    if (response.success) {
      updateState(this, "selectedProduct", response.data);
      updateState(this, "showDetailModal", true);
    } else {
      alert("Ürün detayları alınamadı ❌");
    }
  };

  onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const formObject = Object.fromEntries(formData);
    const response = await ProductApprovalApi.createProduct(formObject);
    if (response.success) {
      updateState(this, "showDetailModal", false);
      updateState(this, "selectedProduct", null);
      this.loadPendingProducts();
    }
  };

  renderTable() {
    return (
      <Table striped bordered hover>
        <thead>
          <tr>
            <th>Stok Kodu</th>
            <th>Ürün Adı</th>
            <th>Barcode</th>
            <th>İşlem</th>
          </tr>
        </thead>
        <tbody>
          {this.state.pendingProducts.map((p) => (
            <tr key={p.id}>
              <td>{p.stockCode}</td>
              <td>{p.name}</td>
              <td>{p.barcode}</td>
              <td>
                <Button size="sm" variant="info" onClick={() => this.openDetailModal(p)}>
                  Ürünü Tanımla
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  }

renderDetailModal = () => {
  const selected = this.state.selectedProduct;
  if (!selected) return null;

  const packagingTypeOptions = PACKAGING_TYPES.map(pg => <option key={pg.key} value={pg.key}>{pg.value}</option>);
  const packagingUnitTypeOptions = UNIT_TYPES.map(u => <option key={u.key} value={u.key}>{u.value}</option>);
  const warehouseStockTypeOptions = WAREHOUSE_STOCK_TYPES.map(ws => <option key={ws.key} value={ws.key}>{ws.value}</option>);

  return (
    <Modal
      autoFocus
      show={this.state.showDetailModal}
      onHide={() => updateState(this, "showDetailModal", false)}
      dialogClassName="modal-90w"
    >
      <Modal.Header closeButton>
        <Modal.Title>Ürün Detayı</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form onSubmit={this.onSubmit}>
          <Form.Control type="hidden" name="id" defaultValue={selected.id || ""} />

          {/* Satır 1: Ürün Adı */}
          <Form.Group className="mb-3">
            <Form.Label>Ürün Adı</Form.Label>
            <Form.Control
              name="name"
              type="text"
              placeholder="Ürün Adı"
              defaultValue={selected.name || ""}
              required
            />
          </Form.Group>

          {/* Satır 2: Stok Kodu ve Barcode */}
          <div className="row mb-3">
            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Stok Kodu</Form.Label>
                <Form.Control
                  name="stockCode"
                  type="text"
                  placeholder="Stok Kodu"
                  defaultValue={selected.stockCode || ""}
                  required
                />
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Barcode</Form.Label>
                <Form.Control
                  name="barcode"
                  type="text"
                  placeholder="Barcode"
                  defaultValue={selected.barcode || ""}
                  required
                />
              </Form.Group>
            </div>
          </div>

          {/* Satır 3: Paketleme Tipi ve Depo Stok Tipi */}
          <div className="row mb-3">
            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Paketleme Tipi</Form.Label>
                <Form.Select
                  name="packagingType"
                  defaultValue={selected.packagingType || ""}
                  disabled={selected.frozen}
                  required
                >
                  <option value="">Seçiniz</option>
                  {packagingTypeOptions}
                </Form.Select>
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Depo Stok Tipi</Form.Label>
                <Form.Select
                  name="warehouseStockType"
                  defaultValue={selected.warehouseStockType || ""}
                  required
                >
                  <option value="">Seçiniz</option>
                  {warehouseStockTypeOptions}
                </Form.Select>
              </Form.Group>
            </div>
          </div>

          {/* Satır 4: Paketleme Ölçü Birimi ve Paket Ölçü Miktarı */}
          <div className="row mb-3">
            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Paketleme Ölçü Birimi</Form.Label>
                <Form.Select
                  name="packagingUnitType"
                  defaultValue={selected.packagingUnitType || ""}
                  disabled={selected.frozen}
                  required
                >
                  <option value="">Seçiniz</option>
                  {packagingUnitTypeOptions}
                </Form.Select>
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Paket Ölçü Miktarı</Form.Label>
                <Form.Control
                  name="packagingUnitTypeAmount"
                  type="text"
                  placeholder="Miktar"
                  defaultValue={selected.packagingUnitTypeAmount || ""}
                  disabled={selected.frozen}
                  required
                />
              </Form.Group>
            </div>
          </div>

          {/* Satır 5: Genişlik, Uzunluk, Yükseklik */}
          <div className="row mb-3">
            <div className="col-md-4">
              <Form.Group>
                <Form.Label>Genişlik (cm)</Form.Label>
                <Form.Control
                  name="width"
                  type="text"
                  placeholder="Genişlik"
                  defaultValue={selected.width || ""}
                  required
                />
              </Form.Group>
            </div>
            <div className="col-md-4">
              <Form.Group>
                <Form.Label>Uzunluk (cm)</Form.Label>
                <Form.Control
                  name="length"
                  type="text"
                  placeholder="Uzunluk"
                  defaultValue={selected.length || ""}
                  required
                />
              </Form.Group>
            </div>
            <div className="col-md-4">
              <Form.Group>
                <Form.Label>Yükseklik (cm)</Form.Label>
                <Form.Control
                  name="height"
                  type="text"
                  placeholder="Yükseklik"
                  defaultValue={selected.height || ""}
                  required
                />
              </Form.Group>
            </div>
          </div>

          {/* Satır 6: Notlar */}
          <Form.Group className="mb-3">
            <Form.Label>Notlar</Form.Label>
            <Form.Control
              as="textarea"
              name="notes"
              rows={5}
              placeholder="Notlarınızı buraya yazınız..."
              defaultValue={selected.notes || ""}
            />
          </Form.Group>

          <Button style={{ float: "right" }} variant="secondary" type="submit">
            Ürünü Tanımla
          </Button>
        </Form>
      </Modal.Body>
    </Modal>
  );
};





  render() {
    return (
      <AuthorizedPage role={Role.SALES_ADMIN}>
        <h4 style={{ marginBottom: "20px" }}>Ürün Onay Sayfası</h4>
        <div className="mb-2">
          <div className="search-container">
            <input
              type="text"
              placeholder="Ara"
              value={this.state.search}
              onChange={(e) => this.onSearchChange(e.target.value)}
              className="search-input"
            />
            <div className="search-icon"></div>
          </div>
        </div>
        {this.renderTable()}
        {this.renderPagination()}
        {this.renderDetailModal()}
      </AuthorizedPage>
    );
  }
}

export default ProductApproval;
